import { FeatureFlag } from '@app/core/services/feature-flags/feature-flag.model';
import { AngularAction } from '@app/pages/checkout-app/checkout-layout/main/components/checkout/react/models/AngularAction';
import { ToastOptions } from '@app/storybook/services/toast/toast-types';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import React, { createContext, useContext, ReactNode } from 'react';
import { Observable, Subject } from 'rxjs';

interface AppContextProps {
  shop$?: Observable<Shop>;
  angularRoutingSubject?: Subject<string>;
  angularActionSubject?: Subject<AngularAction>;
  isFeatureFlagEnabled$?: (flag: FeatureFlag) => Observable<boolean>;
}

interface AppContextValue extends AppContextProps {
  showToast: (toastOptions: ToastOptions) => void;
  showFullPageLoader: (show: boolean) => void;
  angularRedirect: (url: string) => void;
  emitAngularAction: (action: AngularAction) => void;
  refreshNgRxShippings: () => Promise<void>;
}

interface AppProviderProps extends AppContextProps {
  children: ReactNode;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider: React.FC<AppProviderProps> = ({
  children,
  shop$,
  angularRoutingSubject,
  angularActionSubject,
  isFeatureFlagEnabled$,
}) => {
  const showFullPageLoader = (show: boolean) => {
    const action: AngularAction = {
      action: 'show-hide-main-loader',
      payload: { show: show },
      callback: () => {},
    };
    angularActionSubject.next(action);
  };

  const showToast = (toastOptions: ToastOptions) => {
    const action: AngularAction = {
      action: 'toast',
      payload: toastOptions,
      callback: () => {},
    };
    angularActionSubject.next(action);
  };

  const angularRedirect = (url: string) => {
    angularRoutingSubject.next(url);
  };

  const emitAngularAction = (action: AngularAction) => {
    angularActionSubject.next(action);
  };

  const refreshNgRxShippings = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('refreshNgRxShippings timed out'));
      }, 5000);

      const action: AngularAction = {
        action: 'refresh-ngrx-shippings',
        payload: {},
        callback: () => {
          clearTimeout(timeoutId);
          resolve();
        },
      };
      angularActionSubject.next(action);
    });
  };

  const contextValue: AppContextValue = {
    shop$,
    angularRedirect,
    angularActionSubject,
    isFeatureFlagEnabled$,
    showToast,
    showFullPageLoader,
    emitAngularAction,
    refreshNgRxShippings,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
